import styled, { css } from 'styled-components';

const linearGrey = `linear-gradient(
  240deg,
  rgba(255, 255, 255, 0) 0%,
  rgba(255, 255, 255, 0.3) 100%
)`;

export const Container = styled.section`
  width: 80%;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 0 auto;

  @media ${({ theme }) => theme.devices.mobile} {
    width: 100%;
  }
`;

export const GroupTitle = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  user-select: none;

  @media ${({ theme }) => theme.devices.mobile} {
    gap: 8px;
  }

  h1 {
    font-family: 'Anton', sans-serif;
    text-transform: uppercase;
    font-size: 80px;
    text-align: center;

    @media ${({ theme }) => theme.devices.mobile} {
      font-size: 38px;
    }
  }
`;

export const ArrowIcon = styled.span`
  display: inline-block;
  width: 20px;
  height: 20px;
  border-right: 3px solid ${({ theme }) => theme.colors.grey.white};
  border-bottom: 3px solid ${({ theme }) => theme.colors.grey.white};
  transform: ${({ $isExpanded }) =>
    $isExpanded ? 'rotate(-135deg)' : 'rotate(45deg)'};
  transition: transform 0.3s ease;
  margin-bottom: ${({ $isExpanded }) => ($isExpanded ? '0' : '8px')};
  flex-shrink: 0;

  @media ${({ theme }) => theme.devices.mobile} {
    width: 14px;
    height: 14px;
    border-right-width: 2px;
    border-bottom-width: 2px;
  }
`;

export const GroupButton = styled.div`
  width: 100%;
  margin: 20px 0 28px 0;
  gap: 32px;
  display: flex;
  justify-content: space-between;

  @media ${({ theme }) => theme.devices.mobile} {
    gap: 12px;
    margin: 28px 0;
  }
`;

export const Button = styled.button`
  width: 100%;
  padding: 16px;
  position: relative;
  color: ${({ theme }) => theme.colors.grey.white};
  border-radius: 32px;
  border: 0;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 0;

  /* Estado padrão */
  background: ${({ $isActive }) =>
    $isActive
      ? css`linear-gradient(
          90deg,
          rgba(224, 79, 17, 0.4) 0%,
          rgba(228, 35, 32, 0.4) 48%,
          rgba(102, 20, 14, 0.4) 100%
        )`
      : css`
          ${linearGrey}
        `};

  @media ${({ theme }) => theme.devices.mobile} {
    padding: 12px 4px;

    h4 {
      font-size: 16px;
    }
  }

  ${({ $isActive }) =>
    $isActive &&
    css`
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(224, 79, 17, 0.2),
        0 8px 24px rgba(228, 35, 32, 0.15);
    `}

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 32px;
    padding: 2px;
    opacity: 1;
    transition: all 0.4s ease;
    z-index: -1;
    -webkit-mask: linear-gradient(#fff 0 0) content-box,
      linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;

    /* Borda padrão ou ativa */
    background: ${({ $isActive }) =>
      $isActive
        ? css`linear-gradient(
            90deg,
            rgba(224, 79, 17, 1) 0%,
            rgba(228, 35, 32, 1) 48%,
            rgba(102, 20, 14, 1) 100%
          )`
        : css`linear-gradient(
            240deg,
            rgba(255, 255, 255, 0.1) 0%,
            rgba(255, 255, 255, 0.5) 100%
          )`};

    ${({ $isActive }) =>
      $isActive &&
      css`
        animation: subtleShine 2s ease-in-out infinite;
      `}
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(224, 79, 17, 0.2),
      0 8px 24px rgba(228, 35, 32, 0.15);
    background: linear-gradient(
      90deg,
      rgba(224, 79, 17, 0.4) 0%,
      rgba(228, 35, 32, 0.4) 48%,
      rgba(102, 20, 14, 0.4) 100%
    );

    &::after {
      opacity: 1;
      background: linear-gradient(
        90deg,
        rgba(224, 79, 17, 1) 0%,
        rgba(228, 35, 32, 1) 48%,
        rgba(102, 20, 14, 1) 100%
      );
      animation: subtleShine 2s ease-in-out infinite;
    }
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 2px 6px rgba(224, 79, 17, 0.2);
  }

  /* Desabilita hover se estiver ativo */
  ${({ $isActive }) =>
    $isActive &&
    css`
      &:hover {
        transform: translateY(-2px);
      }
    `}

  @keyframes subtleShine {
    0%,
    100% {
      filter: brightness(1);
    }
    50% {
      filter: brightness(1.15);
    }
  }
`;

export const FilterBody = styled.div`
  display: flex;
  flex-direction: column;

  @media ${({ theme }) => theme.devices.mobile} {
    width: 100%;
  }

  > button {
    max-width: 300px;
    margin: 0 auto;
    margin-bottom: 80px;
  }
`;

export const Row = styled.div`
  width: 100%;
  display: flex;
  gap: 32px;
  margin-bottom: 32px;

  @media ${({ theme }) => theme.devices.mobile} {
    width: 100%;
    gap: 8px;
  }
`;

export const Column = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 24px;

  @media ${({ theme }) => theme.devices.mobile} {
    gap: 12px;
  }
`;

export const VerticalDivider = styled.div`
  width: 1px;
  background: ${({ theme }) => theme.colors.grey.white};
`;

export const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  padding: 8px;
  padding-bottom: 32px;
  gap: 4px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.grey.white};

  @media ${({ theme }) => theme.devices.mobile} {
    width: 100%;
    padding: 8px 0;
    padding-bottom: 24px;
  }

  &:last-child {
    border-bottom: none;
  }
`;

export const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: ${({ $wrap }) => ($wrap ? 'wrap' : 'nowrap')};

  @media ${({ theme }) => theme.devices.mobile} {
    flex-direction: column;
    flex-wrap: nowrap;
  }

  ${({ $wrap }) =>
    $wrap &&
    css`
      > button {
        flex: 0 0 calc(50% - 6px); /* 50% menos metade do gap */
        max-width: calc(50% - 6px);

        @media ${({ theme }) => theme.devices.mobile} {
          max-width: 100%;
          flex: none;
        }
      }
    `}
`;

export const FilterButton = styled.button`
  width: 100%;
  padding: 10px 24px;
  border-radius: 32px;
  border: none;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  white-space: nowrap;
  flex: ${({ $wrap }) => ($wrap ? '0 0 auto' : '1')};
  background: ${({ theme }) => theme.colors.grey.white};

  @media ${({ theme }) => theme.devices.mobile} {
    padding: 10px;
    flex: none;
  }

  ${({ $isActive }) =>
    $isActive &&
    css`
      background: linear-gradient(
        90deg,
        rgba(224, 79, 17, 1) 0%,
        rgba(228, 35, 32, 1) 100%
      );
      color: ${({ theme }) => theme.colors.grey.white};
      box-shadow: 0 4px 12px rgba(224, 79, 17, 0.3);
      transform: translateY(-2px);
    `}

  &:active {
    transform: translateY(0);
  }
`;
